import { CONFIG_DISABLE_LEGACY_CURRENCY_FORMATTING } from '@src/config/env'

import type { CurrencyCode, CurrencyFormatConfig, CurrencyFormatName, CurrencyFormatOverrides } from './currency.types'
/**
 * Default currency when region cannot be determined
 */
export const DEFAULT_CURRENCY: CurrencyCode = 'USD'

/**
 * Default locale for formatting when locale cannot be determined
 */
export const DEFAULT_LOCALE: Intl.UnicodeBCP47LocaleIdentifier = 'en-US'

/**
 * Converts internal locale format to BCP 47 format for Intl API
 * @param locale - Internal locale format (e.g., 'en-us', 'fr-ca')
 * @returns BCP 47 locale string (e.g., 'en-US', 'fr-CA')
 */
export function toBCP47Locale(locale: string): string {
  const [language, country] = locale.split('-')
  if (!language || !country) return DEFAULT_LOCALE
  // TODO: Remove this once we enable standard locale configuration
  // eslint-disable-next-line
  if (country === 'ca' && !CONFIG_DISABLE_LEGACY_CURRENCY_FORMATTING) return 'en-CA'
  return `${language}-${country.toUpperCase()}`
}

/**
 * Base currency format configurations using Intl.NumberFormat options
 */
export const BASE_CURRENCY_FORMATS: Record<CurrencyFormatName, CurrencyFormatConfig> = {
  /**
   * SYMBOL_WITH_CODE: Narrow symbol for all currencies, with CAD explicitly prefixed.
   * - USD: $99.99
   * - CAD: CAD $99.99
   */
  SYMBOL_WITH_CODE: {
    options: {
      style: 'currency',
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  },
  NARROW_SYMBOL: {
    options: {
      style: 'currency',
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  },
}

export const DEFAULT_CURRENCY_FORMAT = 'NARROW_SYMBOL' as CurrencyFormatName

/**
 * Helper to format with CAD prefix for SYMBOL_WITH_CODE format
 */
function formatWithCadPrefix(amount: number, locale: string, currency: CurrencyCode): string {
  const bcp47Locale = toBCP47Locale(locale)
  const formatted = new Intl.NumberFormat(bcp47Locale, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)

  return currency === 'CAD' ? `CAD ${formatted}` : formatted
}

/**
 * Currency format overrides for specific locale + format combinations
 * Use when Intl.NumberFormat doesn't produce desired output
 */
export const CURRENCY_FORMAT_OVERRIDES: CurrencyFormatOverrides = {
  'en-ca': {
    SYMBOL_WITH_CODE: {
      formatter: formatWithCadPrefix,
    },
  },
  'fr-ca': {
    SYMBOL_WITH_CODE: {
      formatter: formatWithCadPrefix,
    },
  },
}
